import { IconTemp, IconWind, IconSpeed, IconBumps, IconGrip } from 'icons';

import {
  AIR_TEMPERATURE_CATEGORY,
  TRACK_TEMPERATURE_CATEGORY,
  WIND_CATEGORIES,
  WEATHER_CATEGORIES,
  ROUGHNESS_CATEGORIES,
  TARMAC_CONDITION_CATEGORIES,
} from './enums';
import { FC } from 'react';

type ConditionConfig = {
  icon: FC;
  label: string;
  variants: {
    [key: string]: {
      text: string;
      textColor: string;
      backgroundColor: string;
    };
  };
};

type ConditionsConfig = {
  airTemperature: ConditionConfig;
  trackTemperature: ConditionConfig;
  wind: ConditionConfig;
  rain: ConditionConfig;
  roughness: ConditionConfig;
  tarmacCondition: ConditionConfig;
};

export const conditionsConfig: ConditionsConfig = {
  trackTemperature: {
    icon: IconTemp,
    label: 'Track',
    variants: {
      [TRACK_TEMPERATURE_CATEGORY.extremelyCold]: {
        text: '10-20',
        textColor: 'rgba(0, 255, 255, 1)',
        backgroundColor: 'rgba(0, 255, 255, 0.1)',
      },
      [TRACK_TEMPERATURE_CATEGORY.cold]: {
        text: '20-30',
        textColor: 'rgba(64, 196, 255, 1)',
        backgroundColor: 'rgba(64, 196, 255, 0.1)',
      },
      [TRACK_TEMPERATURE_CATEGORY.moderate]: {
        text: '30-40',
        textColor: 'rgba(178, 255, 89, 1)',
        backgroundColor: 'rgba(178, 255, 89, 0.1)',
      },
      [TRACK_TEMPERATURE_CATEGORY.warm]: {
        text: '40-50',
        textColor: 'rgba(255, 193, 7, 1)',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
      },
      [TRACK_TEMPERATURE_CATEGORY.hot]: {
        text: '50-60',
        textColor: 'rgba(255, 112, 67, 1)',
        backgroundColor: 'rgba(255, 112, 67, 0.1)',
      },
      [TRACK_TEMPERATURE_CATEGORY.extremelyHot]: {
        text: '60-70',
        textColor: 'rgba(255, 82, 82, 1)',
        backgroundColor: 'rgba(255, 82, 82, 0.1)',
      },
      // ... outros variants
    },
  },
  airTemperature: {
    icon: IconTemp,
    label: 'Air',
    variants: {
      [AIR_TEMPERATURE_CATEGORY.extremelyCold]: {
        text: '10-16',
        textColor: 'rgba(0, 255, 255, 1)',
        backgroundColor: 'rgba(0, 255, 255, 0.1)',
      },
      [AIR_TEMPERATURE_CATEGORY.cold]: {
        text: '16-23',
        textColor: 'rgba(64, 196, 255, 1)',
        backgroundColor: 'rgba(64, 196, 255, 0.1)',
      },
      [AIR_TEMPERATURE_CATEGORY.moderate]: {
        text: '23-30',
        textColor: 'rgba(178, 255, 89, 1)',
        backgroundColor: 'rgba(178, 255, 89, 0.1)',
      },
      [AIR_TEMPERATURE_CATEGORY.warm]: {
        text: '30-37',
        textColor: 'rgba(255, 193, 7, 1)',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
      },
      [AIR_TEMPERATURE_CATEGORY.hot]: {
        text: '37-44',
        textColor: 'rgba(255, 112, 67, 1)',
        backgroundColor: 'rgba(255, 112, 67, 0.1)',
      },
      [AIR_TEMPERATURE_CATEGORY.extremelyHot]: {
        text: '44-50',
        textColor: 'rgba(255, 82, 82, 1)',
        backgroundColor: 'rgba(255, 82, 82, 0.1)',
      },
    },
  },
  wind: {
    icon: IconWind,
    label: 'Wind',
    variants: {
      [WIND_CATEGORIES.calm]: {
        text: '0-8',
        textColor: 'rgba(0, 255, 255, 1)',
        backgroundColor: 'rgba(0, 255, 255, 0.1)',
      },
      [WIND_CATEGORIES.breezy]: {
        text: '8-16',
        textColor: 'rgba(64, 196, 255, 1)',
        backgroundColor: 'rgba(64, 196, 255, 0.1)',
      },
      [WIND_CATEGORIES.windy]: {
        text: '16-24',
        textColor: 'rgba(178, 255, 89, 1)',
        backgroundColor: 'rgba(178, 255, 89, 0.1)',
      },
      [WIND_CATEGORIES.blustery]: {
        text: '24-32',
        textColor: 'rgba(255, 193, 7, 1)',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
      },
      [WIND_CATEGORIES.gale]: {
        text: '32-40',
        textColor: 'rgba(255, 112, 67, 1)',
        backgroundColor: 'rgba(255, 112, 67, 0.1)',
      },
      [WIND_CATEGORIES.storm]: {
        text: '40-50',
        textColor: 'rgba(255, 82, 82, 1)',
        backgroundColor: 'rgba(255, 82, 82, 0.1)',
      },
    },
  },
  rain: {
    icon: IconSpeed,
    label: 'Rain',
    variants: {
      [WEATHER_CATEGORIES.dry]: {
        text: 'Dry',
        textColor: 'rgba(255, 255, 153, 1)',
        backgroundColor: 'rgba(255, 255, 153, 0.1)',
      },
      [WEATHER_CATEGORIES.soft]: {
        text: 'Mist',
        textColor: 'rgba(153, 255, 153, 1)',
        backgroundColor: 'rgba(153, 255, 153, 0.1)',
      },
      [WEATHER_CATEGORIES.light]: {
        text: 'Drizzle',
        textColor: 'rgba(102, 255, 255, 1)',
        backgroundColor: 'rgba(102, 255, 255, 0.1)',
      },
      [WEATHER_CATEGORIES.moderate]: {
        text: 'Showers',
        textColor: 'rgba(102, 153, 255, 1)',
        backgroundColor: 'rgba(102, 153, 255, 0.1)',
      },
      [WEATHER_CATEGORIES.heavy]: {
        text: 'Downpour',
        textColor: 'rgba(153, 102, 204, 1)',
        backgroundColor: 'rgba(153, 102, 204, 0.1)',
      },
      [WEATHER_CATEGORIES['very-heavy']]: {
        text: 'Storm',
        textColor: 'rgba(255, 82, 82, 1)',
        backgroundColor: 'rgba(255, 82, 82, 0.1)',
      },
    },
  },
  tarmacCondition: {
    icon: IconGrip,
    label: 'Grip',
    variants: {
      [TARMAC_CONDITION_CATEGORIES.dirty]: {
        text: 'Dirty',
        textColor: 'rgba(255, 82, 82, 1)',
        backgroundColor: 'rgba(255, 82, 82, 0.1)',
      },
      [TARMAC_CONDITION_CATEGORIES.poor]: {
        text: 'Gritty',
        textColor: 'rgba(255, 112, 67, 1)',
        backgroundColor: 'rgba(255, 112, 67, 0.1)',
      },
      [TARMAC_CONDITION_CATEGORIES['below-average']]: {
        text: 'Grainy',
        textColor: 'rgba(255, 165, 0, 1)',
        backgroundColor: 'rgba(255, 165, 0, 0.1)',
      },
      [TARMAC_CONDITION_CATEGORIES.washed]: {
        text: 'Dusted',
        textColor: 'rgba(173, 255, 47, 1)',
        backgroundColor: 'rgba(173, 255, 47, 0.1)',
      },
      [TARMAC_CONDITION_CATEGORIES.good]: {
        text: 'Swept',
        textColor: 'rgba(50, 205, 50, 1)',
        backgroundColor: 'rgba(50, 205, 50, 0.1)',
      },
      [TARMAC_CONDITION_CATEGORIES.perfect]: {
        text: 'Clean',
        textColor: 'rgba(30, 144, 255, 1)',
        backgroundColor: 'rgba(30, 144, 255, 0.1)',
      },
      // ... outros variants
    },
  },
  roughness: {
    icon: IconBumps,
    label: 'Bumps',
    variants: {
      [ROUGHNESS_CATEGORIES['very-smooth']]: {
        text: 'Smooth',
        textColor: 'rgba(102, 255, 255, 1)',
        backgroundColor: 'rgba(102, 255, 255, 0.1)',
      },
      [ROUGHNESS_CATEGORIES.smooth]: {
        text: 'Polished',
        textColor: 'rgba(64, 196, 255, 1)',
        backgroundColor: 'rgba(64, 196, 255, 0.1)',
      },
      [ROUGHNESS_CATEGORIES.average]: {
        text: 'Patchy',
        textColor: 'rgba(30, 144, 255, 1)',
        backgroundColor: 'rgba(30, 144, 255, 0.1)',
      },
      [ROUGHNESS_CATEGORIES.bumpy]: {
        text: 'Wavy',
        textColor: 'rgba(153, 102, 204, 1)',
        backgroundColor: 'rgba(153, 102, 204, 0.1)',
      },
      [ROUGHNESS_CATEGORIES['very-bumpy']]: {
        text: 'Rough',
        textColor: 'rgba(255, 112, 67, 1)',
        backgroundColor: 'rgba(255, 112, 67, 0.1)',
      },
      [ROUGHNESS_CATEGORIES['severe-bumps']]: {
        text: 'Bumpy',
        textColor: 'rgba(255, 82, 82, 1)',
        backgroundColor: 'rgba(255, 82, 82, 0.1)',
      },
      // ... outros variants
    },
  },
};
